import React, { useEffect, useMemo, useRef, useState } from 'react'
import { gql, useQuery } from '@apollo/client'
import { Box, Button, HStack, Menu, MenuButton, MenuItem, MenuList, Text, VStack } from '@chakra-ui/react'
import { SupportedCurrencies, TokenMoney } from '@ninjablaze/sdk-ts'

import { useCashier } from '@/context/CashierProvider'
import { useBalanceStore } from '@/context/UserBalance'
import { getTokenByDenom, getTokens } from '@/services/token-service'
import ChevronDownIcon from '@/public/assets/icons/game-zone/chevron-down.svg'
import { GameZoneModal } from '@/components/molecules/GameZoneModal/GameZoneModal'
import { GameZoneNotice } from '@/components/molecules/GameZoneNotice/GameZoneNotice'
import { GameZoneAmountInput } from '@/components/molecules/GameZoneAmountInput/GameZoneAmountInput'
import { useTranslation } from 'next-i18next/pages'

export const GAME_ZONE_TOKEN_CHANGE_REQUEST_EVENT = 'game-zone:token-change-request'
const GAME_ZONE_MINIMUM_BALANCE: Partial<Record<SupportedCurrencies, number>> = {
  [SupportedCurrencies.INJ]: 0.25,
  [SupportedCurrencies.NBZ]: 50,
  [SupportedCurrencies.SOL]: 0.02,
  [SupportedCurrencies.USDC]: 1,
}

type GameZoneSessionModalProps = {
  onClose: () => void
  currentDenom: string
  embedded?: boolean
  initialDenom?: string
  mode?: 'entry' | 'switch'
  onConfirm: (denom: string) => void | Promise<void>
  onPlayDemo?: () => void
}

type TokenPricesQuery = {
  tokenPrices?: {
    nodes: Array<{ symbol: string; value: string } | null>
  } | null
}

const TOKEN_PRICES_QUERY = gql`
  query GameZoneSessionTokenPrices {
    tokenPrices {
      nodes {
        symbol
        value
      }
    }
  }
`

export const GameZoneSessionModal: React.FC<GameZoneSessionModalProps> = ({
  onClose,
  currentDenom,
  embedded = false,
  initialDenom = currentDenom,
  mode = 'entry',
  onConfirm,
  onPlayDemo,
}) => {
  const { t } = useTranslation('common')
  const balances = useBalanceStore((state) => state.balance)
  const { deposit, withdraw, canDeposit, isProcessing } = useCashier()
  const { data } = useQuery<TokenPricesQuery>(TOKEN_PRICES_QUERY)
  const tokens = getTokens().filter((token) => token.currency !== SupportedCurrencies.SHU)
  const getSelectableDenom = (denom: string) =>
    tokens.some((token) => token.denom === denom) ? denom : (tokens[0]?.denom ?? currentDenom)
  const [selectedDenom, setSelectedDenom] = useState(() => getSelectableDenom(initialDenom))
  const [depositAmount, setDepositAmount] = useState('')

  useEffect(() => {
    setDepositAmount('')
  }, [selectedDenom, mode])
  const [isConfirming, setIsConfirming] = useState(false)
  const didSelectManually = useRef(getSelectableDenom(initialDenom) !== currentDenom)

  useEffect(() => {
    if (initialDenom !== currentDenom) {
      didSelectManually.current = true
      setSelectedDenom(getSelectableDenom(initialDenom))
      setDepositAmount('')
    }
  }, [currentDenom, initialDenom])

  const gameBalances = useMemo(
    () =>
      Object.entries(balances)
        .filter(([, balance]) => balance.game_balance.chainValue.gt(0))
        .map(([denom, balance]) => {
          const token = getTokenByDenom(denom)
          const symbol = token?.currency.toLowerCase()
          const price = Number(
            data?.tokenPrices?.nodes.find((tokenPrice) => tokenPrice?.symbol.toLowerCase() === symbol)
              ?.value ?? (symbol === 'usdc' || symbol === 'usdt' ? 1 : 0)
          )

          return {
            denom,
            amount: balance.game_balance,
            usdValue: balance.game_balance.humanNumber * price,
          }
        }),
    [balances, data]
  )

  const defaultDenom = useMemo(() => {
    const selectableGameBalances = gameBalances.filter(({ denom }) =>
      tokens.some((token) => token.denom === denom)
    )

    if (selectableGameBalances.length === 0) {
      return getSelectableDenom(currentDenom)
    }

    return [...selectableGameBalances].sort((left, right) => {
      const leftValue = left.usdValue || left.amount.humanNumber
      const rightValue = right.usdValue || right.amount.humanNumber
      return rightValue - leftValue
    })[0].denom
  }, [currentDenom, gameBalances])

  useEffect(() => {
    if (mode === 'entry' && !didSelectManually.current) {
      setSelectedDenom(defaultDenom)
    }
  }, [defaultDenom, mode])

  const selectedToken = getTokenByDenom(selectedDenom)
  const selectedBalance = balances[selectedDenom]
  const gameBalance = selectedBalance?.game_balance ?? new TokenMoney(0, selectedDenom)
  const walletBalance = selectedBalance?.amount ?? new TokenMoney(0, selectedDenom)
  const inputAmount = useMemo(
    () =>
      depositAmount ? TokenMoney.fromHuman(depositAmount, selectedDenom) : new TokenMoney(0, selectedDenom),
    [depositAmount, selectedDenom]
  )
  const hasDepositAmount = inputAmount.chainValue.gt(0)
  const canAddFunds = hasDepositAmount && canDeposit(inputAmount, selectedDenom)
  const changingToken = selectedDenom !== currentDenom
  const symbol = selectedToken?.currency.toLowerCase()
  const selectedPrice = Number(
    data?.tokenPrices?.nodes.find((tokenPrice) => tokenPrice?.symbol.toLowerCase() === symbol)?.value ??
      (symbol === 'usdc' || symbol === 'usdt' ? 1 : 0)
  )
  const hasPrice = Number.isFinite(selectedPrice) && selectedPrice > 0
  const gameBalanceUsd = gameBalance.humanNumber * (hasPrice ? selectedPrice : 0)
  const minimumGameBalance = Math.max(
    selectedToken ? (GAME_ZONE_MINIMUM_BALANCE[selectedToken.currency] ?? 0) : 0,
    hasPrice ? 1 / selectedPrice : 0
  )
  const selectedTokenGameBalance =
    gameBalances.find(({ denom }) => denom === selectedDenom)?.amount.humanNumber ?? 0
  const isStartingWithNewToken = mode === 'switch' && changingToken
  const needsTopUp =
    isStartingWithNewToken || gameBalance.chainValue.isZero() || (hasPrice && gameBalanceUsd < 1)
  const projectedGameBalance =
    (isStartingWithNewToken ? 0 : selectedTokenGameBalance) + inputAmount.humanNumber
  const hasMinimumGameBalance = !needsTopUp || projectedGameBalance >= minimumGameBalance
  const otherGameBalances = gameBalances.filter(({ denom }) => denom !== selectedDenom)
  const isBusy = isProcessing || isConfirming
  const canConfirm = hasMinimumGameBalance && (needsTopUp ? canAddFunds : !hasDepositAmount || canAddFunds)

  const handleSelectDenom = (denom: string) => {
    didSelectManually.current = true
    setSelectedDenom(denom)
    setDepositAmount('')
  }

  const handleConfirm = async (addFunds = false) => {
    if (!canConfirm || isBusy) {
      return
    }

    setIsConfirming(true)

    if (mode === 'switch' && changingToken) {
      for (const balance of gameBalances) {
        const success = await withdraw(balance.amount, balance.denom)
        if (!success) {
          setIsConfirming(false)
          return
        }
      }
    } else if (addFunds) {
      for (const balance of otherGameBalances) {
        const success = await withdraw(balance.amount, balance.denom)
        if (!success) {
          setIsConfirming(false)
          return
        }
      }
    }

    if (addFunds) {
      const success = await deposit(inputAmount, selectedDenom)
      if (!success) {
        setIsConfirming(false)
        return
      }
    }

    await onConfirm(selectedDenom)
    setIsConfirming(false)
    onClose()
  }

  const content = (
    <VStack align="stretch" spacing={4}>
      <VStack align="stretch" spacing={1.5}>
        <Text color="ninja.200" fontSize="12px" fontWeight={700} lineHeight={1}>
          {t('game-zone.game-token')}
        </Text>
        <Menu matchWidth>
          <MenuButton
            as={Button}
            variant="secondary"
            h="auto"
            minH="64px"
            px={4}
            py={3}
            whiteSpace="normal"
            bg="ninja.500"
            border="none"
            borderRadius="12px"
            isDisabled={isBusy}
            _hover={{ bg: 'ninja.500' }}
            _active={{ bg: 'ninja.500' }}
          >
            <HStack justify="space-between" spacing={3}>
              <HStack spacing={2} minW={0} flex={1}>
                <Box boxSize="24px" flexShrink={0}>
                  {selectedToken?.icon('24px')}
                </Box>
                <Text fontSize="15px" fontWeight={700} overflowWrap="anywhere" textAlign="left">
                  {selectedToken?.name ?? selectedDenom.toUpperCase()}
                </Text>
              </HStack>
              <VStack align="flex-end" spacing={1} minW={0} maxW="60%" textAlign="right">
                <Text color="ninja.100" fontSize="12px" fontWeight={600} lineHeight={1.3}>
                  {t('game-zone.in-game')}
                </Text>
                <Text
                  color="white"
                  fontSize={{ base: '16px', sm: '18px' }}
                  fontWeight={700}
                  lineHeight={1.2}
                  overflowWrap="anywhere"
                >
                  {gameBalance.humanFormat} {selectedToken?.name ?? selectedDenom.toUpperCase()}
                </Text>
              </VStack>
              <Box as={ChevronDownIcon} boxSize={5} flexShrink={0} />
            </HStack>
          </MenuButton>
          <MenuList
            p={2}
            bg="ninja.600"
            borderColor="ninja.500"
            borderRadius="12px"
            zIndex={1500}
            maxH="min(400px, 50dvh)"
            overflowY="auto"
            overscrollBehavior="contain"
          >
            {tokens.map((token) => {
              const gameBalance = balances[token.denom]?.game_balance
              const walletBalance = balances[token.denom]?.amount
              return (
                <MenuItem
                  key={token.denom}
                  gap={3}
                  px={3}
                  py={2.5}
                  bg={token.denom === selectedDenom ? 'ninja.500' : 'transparent'}
                  borderRadius="8px"
                  onClick={() => handleSelectDenom(token.denom)}
                  _hover={{ bg: 'ninja.500' }}
                >
                  <Box boxSize="20px" flexShrink={0}>
                    {token.icon('20px')}
                  </Box>
                  <Text flex={1} fontSize="14px" fontWeight={700}>
                    {token.name}
                  </Text>
                  <VStack align="stretch" spacing={1} minW={0} maxW="70%">
                    {gameBalance?.chainValue.gt(0) && (
                      <HStack justify="space-between" spacing={2} align="baseline" wrap="wrap">
                        <Text color="ninja.300" fontSize="10px" fontWeight={600}>
                          {t('game-zone.in-game')}
                        </Text>
                        <Text color="ninja.100" fontSize="12px" fontWeight={700} overflowWrap="anywhere">
                          {gameBalance?.humanFormat ?? '0'} {token.name}
                        </Text>
                      </HStack>
                    )}
                    <HStack justify="space-between" spacing={2} align="baseline" wrap="wrap">
                      <Text color="ninja.300" fontSize="10px" fontWeight={600}>
                        {t('game-zone.in-wallet')}
                      </Text>
                      <Text color="ninja.100" fontSize="12px" fontWeight={700} overflowWrap="anywhere">
                        {walletBalance?.humanFormat ?? '0'} {token.name}
                      </Text>
                    </HStack>
                  </VStack>
                </MenuItem>
              )
            })}
          </MenuList>
        </Menu>
      </VStack>

      <VStack align="stretch" spacing={4}>
        <HStack justify="space-between" align="baseline" flexWrap="wrap" rowGap={1} columnGap={3}>
          <Text color="ninja.100" fontSize="12px" fontWeight={600}>
            {t('game-zone.add-from-wallet')}
          </Text>
          <Text color="ninja.200" fontSize="12px" fontWeight={500} ml="auto" textAlign="right">
            {t('game-zone.available', {
              amount: walletBalance.humanFormat,
              currency: selectedToken?.name ?? selectedDenom.toUpperCase(),
            })}
          </Text>
        </HStack>
        <GameZoneAmountInput
          value={depositAmount}
          onChange={setDepositAmount}
          denom={selectedDenom}
          currency={selectedToken?.name ?? selectedDenom.toUpperCase()}
          precision={selectedToken?.precisions ?? 6}
          maxAmount={walletBalance.humanBigNumber}
          isDisabled={isBusy}
        />
        {hasDepositAmount && !canAddFunds && (
          <Text color="brand.error" fontSize="11px">
            {t('game-zone.wallet-limit-error')}
          </Text>
        )}
        {(isStartingWithNewToken || !hasMinimumGameBalance) && (
          <Text color={hasMinimumGameBalance ? 'ninja.200' : 'brand.error'} fontSize="11px">
            {t('game-zone.minimum-balance', {
              amount: minimumGameBalance,
              currency: selectedToken?.name ?? selectedDenom.toUpperCase(),
            })}
          </Text>
        )}
      </VStack>

      <HStack justify="space-between" align="center" spacing={4} minH="44px">
        <Text color="ninja.200" fontSize="14px" fontWeight={500}>
          {t('game-zone.show-balance-as')}
        </Text>
        <Text
          display="flex"
          alignItems="center"
          justifyContent="center"
          h="44px"
          minW="64px"
          px={3}
          bg="ninja.500"
          borderRadius="12px"
          color="ninja.200"
          fontSize="14px"
          fontWeight={700}
        >
          USD
        </Text>
      </HStack>

      {(mode === 'switch' ? changingToken : hasDepositAmount && otherGameBalances.length > 0) && (
        <GameZoneNotice>
          {mode === 'switch'
            ? t('game-zone.switch-notice')
            : t('game-zone.deposit-notice', { currency: selectedToken?.name ?? selectedDenom.toUpperCase() })}
        </GameZoneNotice>
      )}

      <HStack spacing={3} align="stretch">
        {mode !== 'entry' && (
          <Button
            flex={1}
            h="56px"
            variant="secondary"
            borderRadius="12px"
            isDisabled={isBusy}
            onClick={onClose}
          >
            {t('game-zone.cancel')}
          </Button>
        )}
        <Button
          flex={1}
          h="56px"
          size="lg"
          border="none"
          isLoading={isBusy}
          isDisabled={!canConfirm}
          onClick={() => handleConfirm(hasDepositAmount)}
        >
          {mode === 'entry'
            ? hasDepositAmount
              ? t('game-zone.add-funds-and-start')
              : t('game-zone.start-game')
            : hasDepositAmount
              ? changingToken
                ? t('game-zone.withdraw-add-switch')
                : t('game-zone.add-funds-and-continue')
              : changingToken
                ? t('game-zone.withdraw-and-switch')
                : t('game-zone.continue')}
        </Button>
      </HStack>

      {mode === 'entry' && onPlayDemo && (
        <Button
          variant="link"
          color="ninja.200"
          fontSize="14px"
          fontWeight={600}
          isDisabled={isBusy}
          onClick={onPlayDemo}
          _hover={{ color: 'white', textDecoration: 'underline' }}
        >
          {t('game-zone.play-demo')}
        </Button>
      )}
    </VStack>
  )

  return (
    <GameZoneModal
      variant={mode}
      title={t(mode === 'entry' ? 'game-zone.choose-currency' : 'game-zone.change-currency')}
      description={t('game-zone.switch-description')}
      onClose={onClose}
      embedded={embedded}
      isBusy={isBusy}
      isClosable={mode !== 'entry'}
      showClose={mode !== 'entry'}
    >
      {content}
    </GameZoneModal>
  )
}
